import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import storage from 'redux-persist/lib/storage'
import { persistReducer } from 'redux-persist'
import { ChannelType } from '@mobi/component-library/Common/Svg/SkyTv/SkyTvSvg'
import { displayVisionNotEntitledMessage } from '../Components/VideoPlayer/helpers/useEntitlement'
import { isReactNativeApp } from '@mobi/web-native-comms/web'

interface RaceReplayTitle {
  raceNumber?: number
  trackName?: string
  position?: string
  starterName?: string
  starterNumber?: number
}

const skyVideoPlayerInitialState: SkyVideoPlayerState = {
  selectedChannel: 'Sky1',
  playerStatus: 'floating',
  previousPlayerStatus: 'floating',
  isPlayerOpen: false,
  isPlaying: false,
  isMuted: true,
  isFullscreen: false,
  isControlShowing: false,
  isEntitled: null,
  dockedPlayerHeight: null,

  isPendingBetsDrawerOpen: false,
  canRenderPendingBetsDrawer: false,

  raceReplayKey: '',
  raceReplayUrl: '',
  raceReplayUrlChanged: false,
  raceReplayTitle: undefined,
  raceReplayMeetingDate: '',

  raceReplayElapsedSeconds: 0,
  raceReplayDurationSeconds: 0,
}

const skyVideoPlayerSlice = createSlice({
  name: 'sky-video-player',
  initialState: skyVideoPlayerInitialState,
  reducers: {
    setIsFullscreen(state, { payload: isFullscreen }: PayloadAction<boolean>) {
      state.isFullscreen = isFullscreen
    },

    setIsPendingBetsDrawerOpen(
      state,
      { payload: isPendingBetsDrawerOpen }: PayloadAction<boolean>
    ) {
      state.isPendingBetsDrawerOpen = isPendingBetsDrawerOpen
    },

    setCanRenderPendingBetsDrawer(
      state,
      { payload: isEnoughRoomToRender }: PayloadAction<boolean>
    ) {
      state.canRenderPendingBetsDrawer = isEnoughRoomToRender
    },

    setIsPlaying(state, { payload: isPlaying }: PayloadAction<boolean>) {
      state.isPlaying = isPlaying
      if (!isPlaying) {
        state.isFullscreen = false
      }
    },

    setIsMuted(state, { payload: isMuted }: PayloadAction<boolean>) {
      state.isMuted = isMuted
    },

    setSelectedChannel(
      state,
      { payload: selectedChannel }: PayloadAction<SkyVideoPlayerState['selectedChannel']>
    ) {
      if (state.selectedChannel === selectedChannel) return

      state.selectedChannel = selectedChannel
      state.isPlaying = true
    },

    setIsControlShowing(state, { payload }: PayloadAction<boolean | null>) {
      state.isControlShowing = payload === null ? !state.isControlShowing : payload
    },

    setEntitlementStatus(
      state,
      { payload }: PayloadAction<{ isEntitled: boolean; notEntitledMessage?: string }>
    ) {
      state.isEntitled = payload.isEntitled
      if (!payload.isEntitled) {
        state.notEntitledMessage = payload.notEntitledMessage
      }
    },

    setSkyVideoPlayerStatus(state, { payload }: PayloadAction<SetSkyVideoPlayerStatusOptions>) {
      if (state.isEntitled === false) {
        displayVisionNotEntitledMessage(state.notEntitledMessage || '')
        return
      }

      const newPreviousPlayerStatus = state.playerStatus
      state.playerStatus = payload.playerStatus || state.previousPlayerStatus
      state.previousPlayerStatus = newPreviousPlayerStatus

      state.isPlayerOpen = true
      state.isPlaying = true
      state.isMuted = payload.isMuted || state.isMuted
      state.selectedChannel = payload.selectedChannel || state.selectedChannel
      state.isFullscreen = false
      state.isPendingBetsDrawerOpen = false

      state.raceReplayKey = payload.raceReplayKey || state.raceReplayKey

      const raceReplayUrl = payload.raceReplayUrl || state.raceReplayUrl
      state.raceReplayUrlChanged = raceReplayUrl != state.raceReplayUrl
      state.raceReplayUrl = raceReplayUrl
      state.raceReplayTitle = payload.raceReplayTitle || state.raceReplayTitle
      state.raceReplayMeetingDate = payload.raceReplayMeetingDate || state.raceReplayMeetingDate
    },

    setCloseSkyVideoPlayer(state) {
      state.isPlayerOpen = false
      resetRaceReplayState(state)
    },

    setSkyDockedPlayerHeight(state, { payload }: PayloadAction<number | null>) {
      state.dockedPlayerHeight = payload
    },

    resetSkyVideoPlayerPersistedState(state) {
      state.isPlayerOpen = skyVideoPlayerInitialState.isPlayerOpen
      state.selectedChannel = skyVideoPlayerInitialState.selectedChannel
      state.isPlaying = skyVideoPlayerInitialState.isPlaying
      state.isMuted = skyVideoPlayerInitialState.isMuted

      resetRaceReplayState(state)
    },

    setRaceReplayElapsedSeconds(state, { payload }: PayloadAction<number>) {
      state.raceReplayElapsedSeconds = Math.ceil(payload)
    },

    setRaceReplayDurationSeconds(state, { payload }: PayloadAction<number>) {
      state.raceReplayDurationSeconds = Math.ceil(payload)
    },
  },
})

function resetRaceReplayState(state: SkyVideoPlayerState) {
  state.raceReplayKey = skyVideoPlayerInitialState.raceReplayKey
  state.raceReplayUrl = skyVideoPlayerInitialState.raceReplayUrl

  state.raceReplayTitle = skyVideoPlayerInitialState.raceReplayTitle
  state.raceReplayMeetingDate = skyVideoPlayerInitialState.raceReplayMeetingDate

  state.raceReplayElapsedSeconds = skyVideoPlayerInitialState.raceReplayElapsedSeconds
  state.raceReplayDurationSeconds = skyVideoPlayerInitialState.raceReplayDurationSeconds
}

export const {
  resetSkyVideoPlayerPersistedState,
  setIsFullscreen,
  setIsPlaying,
  setSkyVideoPlayerStatus,
  setCloseSkyVideoPlayer,
  setIsPendingBetsDrawerOpen,
  setCanRenderPendingBetsDrawer,
  setIsMuted,
  setSelectedChannel,
  setIsControlShowing,
  setEntitlementStatus,
  setSkyDockedPlayerHeight,

  setRaceReplayDurationSeconds,
  setRaceReplayElapsedSeconds,
} = skyVideoPlayerSlice.actions

const persistKeys: StateKeys[] = isReactNativeApp
  ? ['isPlayerOpen', 'selectedChannel', 'isPlaying', 'isMuted']
  : []

export default persistReducer(
  {
    key: 'sky-player',
    whitelist: persistKeys,
    storage,
    debug: !PRODUCTION,
  },
  skyVideoPlayerSlice.reducer
)

// =====
// Types
// =====

export interface SkyVideoPlayerState {
  selectedChannel: ChannelType
  playerStatus: 'floating' | 'docked' | 'fullscreen'
  previousPlayerStatus: 'floating' | 'docked' | 'fullscreen'
  isPlayerOpen: boolean
  isPlaying: boolean
  isMuted: boolean
  isFullscreen: boolean
  isControlShowing: boolean
  isEntitled: null | boolean
  dockedPlayerHeight: null | number
  notEntitledMessage?: string
  isPendingBetsDrawerOpen: boolean
  canRenderPendingBetsDrawer: boolean

  raceReplayKey: string
  raceReplayUrl: string
  raceReplayUrlChanged: boolean
  raceReplayTitle: RaceReplayTitle | undefined
  raceReplayMeetingDate: string
  raceReplayElapsedSeconds: number
  raceReplayDurationSeconds: number
}

type SetSkyVideoPlayerStatusOptions = Partial<
  Pick<
    SkyVideoPlayerState,
    | 'playerStatus'
    | 'selectedChannel'
    | 'isMuted'
    | 'raceReplayKey'
    | 'raceReplayUrl'
    | 'raceReplayTitle'
    | 'raceReplayMeetingDate'
  >
>

type StateKeys = keyof SkyVideoPlayerState
